/**
 * CameraSettingsManager - Manages camera settings persistence and restoration
 *
 * This manager handles:
 * - Defining camera setting types (2D_TOP, 2D_PLANE, 3D_PERSPECTIVE)
 * - Storing/restoring camera rotation (yaw) per camera setting type
 * - Dispatching events when camera settings change
 *
 * Camera settings are separated from shading settings, allowing:
 * - Same rotation to be preserved when switching between different shading modes
 * - Different rotations per camera type (e.g., 2D top view vs 2D plane view)
 */

import { EventDispatcher } from '../EventDispatcher';
import { Viewer } from '../viewer/viewer';
import { CameraMode } from '../defines';
import { detectRoomOrientationFromPolygon, detectRoomOrientationFromPointCloud, RoomOrientationResult } from '../utils/RoomOrientationDetector';
import * as THREE from 'three';

/**
 * Camera setting type identifiers
 * These define the different camera configurations that can have their own rotation
 */
export enum CameraSettingType {
  /** Top-down orthographic view (map view) */
  ORTHOGRAPHIC_TOP = 'orthographic_top',

  /** Plane-aligned orthographic view (floor/wall views) */
  ORTHOGRAPHIC_PLANE = 'orthographic_plane',

  /** Side profile orthographic view */
  ORTHOGRAPHIC_SIDE = 'orthographic_side',

  /** Standard 3D perspective view */
  PERSPECTIVE_3D = 'perspective_3d',
}

/**
 * Camera settings that can be persisted
 */
export interface CameraSettings {
  /**
   * Screen heading in radians, interpreted on the setting type's canonical
   * axis: ROLL for the 2D orthographic classes (top/plane/side), YAW for
   * PERSPECTIVE_3D. One value, one axis per class — see
   * personas/orientation.ts `headingAxis`.
   */
  heading: number;
}

/**
 * Event types dispatched by CameraSettingsManager
 */
export enum CameraSettingsEvents {
  /** Camera rotation was changed (e.g., by alignment tool) */
  ROTATION_CHANGED = 'camera_rotation_changed',

  /** Camera settings were restored from storage */
  SETTINGS_RESTORED = 'camera_settings_restored',
}

/**
 * Event data for rotation changed event
 */
export interface CameraRotationChangedEvent {
  type: CameraSettingsEvents.ROTATION_CHANGED;
  cameraSettingType: CameraSettingType;
  /** Screen heading in radians (see CameraSettings.heading). */
  heading: number;
  /** Source of the change (e.g., 'alignment_tool', 'manual') */
  source: string;
}

export class CameraSettingsManager extends EventDispatcher {
  private viewer: Viewer;

  /** In-memory storage for camera settings per type */
  private settings: Map<CameraSettingType, CameraSettings> = new Map();

  /** Cached detected room orientation (computed once per session) */
  private detectedRoomOrientation: number | null = null;
  private roomOrientationDetected = false;

  /** Guards concurrent background detection kicks from ensureDetectedRotation. */
  private detectionInFlight = false;

  constructor(viewer: Viewer) {
    super();
    this.viewer = viewer;
  }

  /**
   * Determine the camera setting type from the current view state
   */
  getCameraSettingType(): CameraSettingType {
    const viewerMode = this.viewer.viewerMode;
    const cameraMode = this.viewer.scene.cameraMode;

    if (cameraMode === CameraMode.PERSPECTIVE) {
      return CameraSettingType.PERSPECTIVE_3D;
    }

    // Orthographic mode - determine which type
    if (viewerMode?.in2DMode) {
      if (viewerMode.hasVirtualPlane && !viewerMode.associatedLine) {
        // Map view (virtual plane at z=0)
        return CameraSettingType.ORTHOGRAPHIC_TOP;
      } else if (viewerMode.associatedPlane) {
        // Check if it's a side view (looking along Y axis)
        const normal = viewerMode.associatedPlane.normal;
        if (Math.abs(normal.y) > 0.9) {
          return CameraSettingType.ORTHOGRAPHIC_SIDE;
        }
        // Plane-aligned view (floor, wall, etc.)
        return CameraSettingType.ORTHOGRAPHIC_PLANE;
      }
    }

    // Default to top view for orthographic
    return CameraSettingType.ORTHOGRAPHIC_TOP;
  }

  /**
   * Get camera setting type from a view mode definition
   */
  getCameraSettingTypeFromViewMode(cameraMode?: (typeof CameraMode)[keyof typeof CameraMode], planeId?: string): CameraSettingType {
    if (cameraMode === CameraMode.PERSPECTIVE) {
      return CameraSettingType.PERSPECTIVE_3D;
    }

    // For orthographic, we need more context.
    // 'side' preserves its own (non-top-down) orientation.
    if (planeId === 'side') {
      return CameraSettingType.ORTHOGRAPHIC_SIDE;
    }

    // 'map' and planeless orthographic views (X-Ray, 2D-top) are top-down.
    // A bare orthographic 2D view never carries a planeId in this codebase —
    // plane/line/surface views always pass an identifier — so treating
    // "no planeId" as top-down keeps X-Ray's saved-roll restoration consistent
    // with the runtime `enable2DMode('map')` path.
    if (planeId === 'map' || !planeId) {
      return CameraSettingType.ORTHOGRAPHIC_TOP;
    }

    // Plane-specific views (floor, wall, etc.) — no saved top-down roll restoration.
    return CameraSettingType.ORTHOGRAPHIC_PLANE;
  }

  /**
   * Save current camera rotation for the specified camera setting type
   */
  saveRotation(cameraSettingType: CameraSettingType, heading: number, source = 'manual'): void {
    const settings = this.settings.get(cameraSettingType) || { heading: 0 };
    settings.heading = heading;
    this.settings.set(cameraSettingType, settings);

    // Dispatch event so client can persist to database
    this.dispatchEvent({
      type: CameraSettingsEvents.ROTATION_CHANGED,
      cameraSettingType,
      heading,
      source,
    });
  }

  /**
   * Save the current camera rotation for the current view
   */
  saveCurrentRotation(source = 'manual'): void {
    const cameraSettingType = this.getCameraSettingType();
    const roll = this.viewer.scene.view.roll;
    this.saveRotation(cameraSettingType, roll, source);
  }

  /**
   * Get saved rotation for a camera setting type (synchronous, no auto-detection)
   * Returns undefined if no rotation has been saved
   */
  getRotation(cameraSettingType: CameraSettingType): number | undefined {
    return this.settings.get(cameraSettingType)?.heading;
  }

  /**
   * Kick off room-orientation detection in the background when no rotation is
   * saved yet for ORTHOGRAPHIC_TOP. NEVER awaited on a view-switch path — the
   * switch applies the sync `getRotation()` value (or leaves roll alone) and
   * this fills the store for next time.
   *
   * When detection lands, it is saved via saveRotation (detectRoomOrientation
   * self-saves, as before) and applied to the LIVE view only when still safe:
   * the camera is still in a top-down orthographic view AND the user hasn't
   * rotated since the kickoff. Otherwise the result just waits for the next
   * top-down entry. This removes the LOD-streaming nondeterminism and the
   * zero-roll flash that awaiting detection mid-transition caused.
   */
  ensureDetectedRotation(): void {
    if (this.getRotation(CameraSettingType.ORTHOGRAPHIC_TOP) !== undefined) return;
    if (this.detectionInFlight) return;
    this.detectionInFlight = true;

    const rollAtKickoff = this.viewer.scene.view.roll;
    void this.detectRoomOrientation()
      .then((result) => {
        if (result === null) return;
        const stillTopDown = this.viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC && this.getCameraSettingType() === CameraSettingType.ORTHOGRAPHIC_TOP;
        const unrotated = this.viewer.scene.view.roll === rollAtKickoff;
        if (stillTopDown && unrotated) {
          this.viewer.scene.view.roll = result.angle;
        }
      })
      .catch((error) => {
        console.error('[CameraSettingsManager] Background room-orientation detection failed:', error);
      })
      .finally(() => {
        this.detectionInFlight = false;
      });
  }

  /**
   * Detect room orientation from available data (rooms, point cloud)
   * Results are cached for the session
   * @param centerPoint - Optional center point for ray casting. If provided, used directly.
   *                      The caller (e.g. CameraOrientationTool) should calculate this based on
   *                      mouse position X/Y + floor mid Z.
   * @returns Full detection result including plane info for visualization, or null if detection failed
   */
  async detectRoomOrientation(centerPoint: THREE.Vector3 | null = null): Promise<RoomOrientationResult | null> {
    // Return cached result if already computed (only when no center point provided)
    // Note: cached result only has angle, not full plane info
    if (this.roomOrientationDetected && !centerPoint && this.detectedRoomOrientation !== null) {
      // Return minimal result for cached angle (no plane visualization data)
      const cachedNormal = new THREE.Vector3(1, 0, 0);
      return {
        angle: this.detectedRoomOrientation,
        confidence: 1.0,
        wallSampleCount: 0,
        wallDirections: [],
        wallInliers: [],
        wallNormal: cachedNormal,
        wallPlane: new THREE.Plane(cachedNormal, 0),
      };
    }

    this.roomOrientationDetected = true;

    // Try to detect from existing room polygons first (faster) - only if no center point provided
    if (!centerPoint) {
      const rooms = this.viewer.scene.rooms;
      if (rooms && rooms.length > 0) {
        // Find the largest room by area
        let largestRoom = rooms[0];
        let largestArea = 0;

        for (const room of rooms) {
          const area = room.getArea();
          if (area > largestArea) {
            largestArea = area;
            largestRoom = room;
          }
        }

        const polygon = largestRoom.getPolygon();
        if (polygon && polygon.length >= 3) {
          const orientation = detectRoomOrientationFromPolygon(polygon);
          if (orientation !== null) {
            this.detectedRoomOrientation = orientation;

            // Save the detected orientation so it persists
            this.saveRotation(CameraSettingType.ORTHOGRAPHIC_TOP, orientation, 'auto_detected_from_room');

            // Return result with angle but no plane visualization data
            const roomNormal = new THREE.Vector3(Math.cos(orientation), Math.sin(orientation), 0);
            return {
              angle: orientation,
              confidence: 1.0,
              wallSampleCount: 0,
              wallDirections: [orientation],
              wallInliers: [],
              wallNormal: roomNormal,
              wallPlane: new THREE.Plane(roomNormal, 0),
            };
          }
        }
      }
    }

    // Try to detect from point cloud data
    const pointclouds = this.viewer.scene.pointclouds;
    if (pointclouds && pointclouds.length > 0) {
      const pointcloud = pointclouds[0];

      // Check if pointcloud has visible nodes (required for ray casting)
      if (!pointcloud.visibleNodes || pointcloud.visibleNodes.length === 0) {
        // Point cloud not yet rendered, can't detect orientation
        // Reset the flag so we can retry later
        this.roomOrientationDetected = false;
        return null;
      }

      // Determine the center point for ray casting
      let center: THREE.Vector3 | null = null;

      if (centerPoint) {
        // Use the provided center point directly (caller already calculated proper X/Y/Z)
        center = centerPoint.clone();
      } else {
        // No center point provided - use bounding box center as default
        const boundingBox = pointcloud.boundingBox;
        if (boundingBox) {
          const worldBoundingBox = boundingBox.clone().applyMatrix4(pointcloud.matrixWorld);
          center = new THREE.Vector3();
          worldBoundingBox.getCenter(center);
        }
      }

      if (center) {
        const result = await detectRoomOrientationFromPointCloud(pointcloud, center, {
          numRays: 100,
          maxDistance: 20,
          samplingRadius: 0.05, // Increase sampling radius to capture more wall points
          verticalLevels: 3, // Sample more vertical levels
          verticalSpacing: 0.25, // Closer spacing
          proximityClusterRadius: 0.35, // Filter isolated outliers
          minClusterSize: 10, // Require at least 10 nearby points
          translate: (source) => this.viewer.translationService.t(source),
          viewer: this.viewer, // Enable debug visualization
        });

        if (result && result.confidence > 0.3) {
          this.detectedRoomOrientation = result.angle;

          // Save the detected orientation so it persists
          this.saveRotation(CameraSettingType.ORTHOGRAPHIC_TOP, result.angle, 'auto_detected');

          return result;
        }
      }
    }

    return null;
  }

  /**
   * Reset the cached room orientation (e.g., when rooms change)
   */
  resetRoomOrientationCache(): void {
    this.roomOrientationDetected = false;
    this.detectedRoomOrientation = null;
  }

  /**
   * Restore rotation from saved settings
   * Called when switching to a view that has saved rotation
   */
  restoreRotation(cameraSettingType: CameraSettingType): boolean {
    const settings = this.settings.get(cameraSettingType);
    if (settings === undefined) {
      return false;
    }

    this.viewer.scene.view.roll = settings.heading;

    this.dispatchEvent({
      type: CameraSettingsEvents.SETTINGS_RESTORED,
      cameraSettingType,
      heading: settings.heading,
    });

    return true;
  }

  /**
   * Load settings from external source (e.g., database)
   * Called by client when project loads
   */
  loadSettings(cameraSettingType: CameraSettingType, settings: CameraSettings): void {
    this.settings.set(cameraSettingType, settings);
  }

  /**
   * Check if a camera setting type has saved settings
   */
  hasSettings(cameraSettingType: CameraSettingType): boolean {
    return this.settings.has(cameraSettingType);
  }

  /**
   * Clear all saved settings
   */
  clearSettings(): void {
    this.settings.clear();
  }

  /**
   * Get all saved settings (for serialization)
   */
  getAllSettings(): Map<CameraSettingType, CameraSettings> {
    return new Map(this.settings);
  }
}
